import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import {
  Truck, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Link2, X,
  ChevronDown, AlertCircle,
} from 'lucide-react';

/**
 * DelhiveryWarehouseHealthCard — admin dashboard widget.
 *
 * Features:
 *  • Bulk "Register All Warehouses" backfill
 *  • Per-vendor Retry / Force Re-register buttons
 *  • NEW: "Link" — manually associate an ACTIVE Delhivery warehouse name
 *    to a vendor's location when our DB name and Delhivery's active name diverge
 *    (the #1 cause of "non serviceable pincode" AWB errors)
 */
export default function DelhiveryWarehouseHealthCard() {
  const { apiCall } = useAuth();
  const { toast } = useToast();

  const [running, setRunning]           = useState(false);
  const [stats, setStats]               = useState(null);
  const [showDetails, setShowDetails]   = useState(false);
  const [registeringId, setRegisteringId] = useState(null);
  const [lastRunAt, setLastRunAt]       = useState(null);

  // Delhivery live warehouses (from their API)
  const [dlWarehouses, setDlWarehouses]   = useState(null);
  const [loadingDl, setLoadingDl]         = useState(false);

  // Link-warehouse modal state
  const [linkRow, setLinkRow]             = useState(null);  // the backfill row being linked
  const [linkName, setLinkName]           = useState('');
  const [linking, setLinking]             = useState(false);

  // ── Load backfill status ──────────────────────────────────────────────────

  const loadLastResult = useCallback(async () => {
    try {
      const res = await apiCall('/api/shipping/admin/delhivery/backfill-warehouses-status');
      if (res?.result) { setStats(res.result); setLastRunAt(res.last_run_at); }
    } catch (_) { /* not run yet */ }
  }, [apiCall]);

  useEffect(() => { loadLastResult(); }, [loadLastResult]);

  // ── Load Delhivery live warehouse list ────────────────────────────────────

  // Delhivery API error state (separate from toast so it persists in UI)
  const [dlError, setDlError] = useState(null);

  const loadDelhiveryWarehouses = useCallback(async () => {
    setLoadingDl(true);
    setDlError(null);
    try {
      const res = await apiCall('/api/shipping/admin/delhivery/list-warehouses');
      setDlWarehouses(res);
    } catch (e) {
      // e.message contains the flattened detail from our 424 response
      const msg = e?.message || 'Unknown error';
      setDlError(msg);
      toast({ title: 'Could not load Delhivery warehouses', description: msg, variant: 'destructive' });
    } finally {
      setLoadingDl(false);
    }
  }, [apiCall, toast]);

  // Build a Set of names that are ACTIVE in Delhivery (from list API)
  const activeDlNames = new Set((dlWarehouses?.warehouses || []).map(w => w.name).filter(Boolean));

  // ── Actions ───────────────────────────────────────────────────────────────

  const runBackfill = async () => {
    setRunning(true);
    try {
      toast({ title: 'Registration started', description: 'Walking through every vendor pickup location at 1 req/s…' });
      const res = await apiCall('/api/shipping/admin/delhivery/backfill-warehouses', { method: 'POST' });
      setStats(res);
      setLastRunAt(new Date().toISOString());
      toast({
        title: res.failed > 0 ? 'Done with some failures' : 'All done',
        description: `Registered ${res.registered}, already done ${res.already}, failed ${res.failed} of ${res.total}.`,
      });
    } catch (e) {
      toast({ title: 'Backfill failed', description: e?.message || 'Unexpected error', variant: 'destructive' });
    } finally { setRunning(false); }
  };

  const registerOne = async (row, force = false) => {
    if (!row.location_id) return;
    setRegisteringId(row.location_id);
    try {
      const endpoint = force
        ? '/api/shipping/admin/delhivery/force-reregister-warehouse'
        : '/api/shipping/admin/delhivery/register-warehouse';
      const res = await apiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify({ vendor_location_id: row.location_id }),
      });
      if (res?.success) {
        toast({ title: force ? 'Force Re-registered' : 'Registered',
          description: `New warehouse name: ${res.new_warehouse_name || res.warehouse_name}` });
        setStats(prev => prev ? {
          ...prev,
          details: prev.details.map(d => d.location_id === row.location_id
            ? { ...d, status: 'ok', warehouse_name: res.new_warehouse_name || res.warehouse_name, message: res.message, missing_fields: [] }
            : d),
        } : prev);
      } else {
        toast({ title: 'Still failing', description: res?.message || 'See message in row', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e?.message || 'Try again.', variant: 'destructive' });
    } finally { setRegisteringId(null); }
  };

  // Link an existing Delhivery warehouse name to a vendor_location
  const openLinkModal = (row) => {
    setLinkRow(row);
    // Pre-fill with current name if it exists
    setLinkName(row.warehouse_name || '');
    // Auto-load warehouse list the first time
    if (!dlWarehouses) loadDelhiveryWarehouses();
  };

  const confirmLink = async () => {
    if (!linkRow?.location_id || !linkName.trim()) return;
    setLinking(true);
    try {
      const res = await apiCall('/api/shipping/admin/delhivery/link-warehouse', {
        method: 'POST',
        body: JSON.stringify({ location_id: linkRow.location_id, delhivery_warehouse_name: linkName.trim() }),
      });
      if (res?.success) {
        toast({ title: 'Warehouse linked', description: `${linkRow.vendor_business_name} → ${linkName.trim()}` });
        setStats(prev => prev ? {
          ...prev,
          details: prev.details.map(d => d.location_id === linkRow.location_id
            ? { ...d, status: 'ok', warehouse_name: linkName.trim(), message: 'Manually linked' }
            : d),
        } : prev);
        setLinkRow(null);
      } else {
        toast({ title: 'Link failed', description: res?.message, variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e?.message, variant: 'destructive' });
    } finally { setLinking(false); }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const failedCount = stats?.failed || 0;

  // A row's DB warehouse name is "mismatched" if activeDlNames is loaded and
  // the stored name is not in the live Delhivery account
  const isMismatched = (row) => {
    if (!activeDlNames.size) return false;
    if (!row.warehouse_name) return false;
    return !activeDlNames.has(row.warehouse_name);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Card data-testid="delhivery-warehouse-health-card">
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Truck className="h-4 w-4 text-amber-700 shrink-0" />
                <h3 className="font-semibold text-sm">Delhivery Warehouse Registration</h3>
                {stats && (
                  failedCount === 0
                    ? <Badge className="bg-green-100 text-green-700 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-0.5" /> All registered</Badge>
                    : <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px]"><AlertTriangle className="h-3 w-3 mr-0.5" /> {failedCount} failed</Badge>
                )}
              </div>
              <p className="text-xs text-gray-600 max-w-xl">
                Registers every vendor pickup location with Delhivery. Use <b>Link</b> to fix "non serviceable pincode" errors by pointing a vendor's location to an <em>existing active</em> Delhivery warehouse.
              </p>
              {stats && (
                <div className="mt-2 text-xs text-gray-700 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>Total: <b>{stats.total}</b></span>
                  <span className="text-green-700">Registered: <b>{stats.registered}</b></span>
                  <span className="text-blue-700">Already: <b>{stats.already}</b></span>
                  <span className={failedCount > 0 ? 'text-amber-700' : 'text-gray-500'}>Failed: <b>{stats.failed}</b></span>
                  {lastRunAt && <span className="text-gray-400">· last run {new Date(lastRunAt).toLocaleString()}</span>}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <Button size="sm" onClick={runBackfill} disabled={running} data-testid="delhivery-backfill-btn">
                {running ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Registering…</>
                  : stats ? <><RefreshCw className="h-3 w-3 mr-1" /> Re-run Registration</>
                  : <><Truck className="h-3 w-3 mr-1" /> Register All Warehouses</>}
              </Button>
              <div className="flex gap-2">
                {stats?.details?.length > 0 && (
                  <Button size="sm" variant="ghost" className="h-7 text-xs"
                    onClick={() => setShowDetails(!showDetails)}
                    data-testid="delhivery-backfill-toggle-details">
                    {showDetails ? 'Hide' : 'Show'} details ({stats.details.length})
                  </Button>
                )}
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={loadDelhiveryWarehouses}
                  disabled={loadingDl} data-testid="load-delhivery-warehouses-btn"
                  title="Load live warehouse list from Delhivery API to check for name mismatches">
                  {loadingDl ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RefreshCw className="h-3 w-3 mr-1" /> Check Delhivery</>}
                </Button>
              </div>
            </div>
          </div>

          {/* Live Delhivery warehouses summary strip */}
          {dlError && (
            <div className="mt-3 border rounded-lg p-3 bg-red-50 border-red-200 text-xs" data-testid="delhivery-list-error">
              <div className="font-medium text-red-800 flex items-center gap-1.5 mb-1">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                Delhivery API Error
              </div>
              <p className="text-red-700 break-words">{dlError}</p>
              <p className="mt-1.5 text-red-600 font-medium">
                Most likely cause: this server's outbound IP is not whitelisted in the Delhivery API portal.
                Go to Delhivery &gt; Settings &gt; API Keys &gt; IP Whitelist and add the production server IP.
              </p>
            </div>
          )}
          {dlWarehouses && (
            <div className="mt-3 border rounded-lg p-3 bg-blue-50 border-blue-200 text-xs">
              <div className="font-medium text-blue-800 mb-1">
                Delhivery account: {dlWarehouses.total} active warehouse(s)
              </div>
              <div className="flex flex-wrap gap-2">
                {(dlWarehouses.warehouses || []).map(w => (
                  <span key={w.name}
                    className={`font-mono px-2 py-0.5 rounded text-[11px] ${
                      w.linked_in_our_db
                        ? 'bg-green-100 text-green-800 border border-green-300'
                        : 'bg-amber-100 text-amber-800 border border-amber-300'
                    }`}
                    title={`${w.city} · ${w.pin}${w.linked_in_our_db ? ' · Linked in our DB' : ' · NOT linked in our DB'}`}>
                    {w.name}
                    {w.linked_in_our_db
                      ? <span className="ml-1 text-green-600">✓</span>
                      : <span className="ml-1 text-amber-600">!</span>}
                  </span>
                ))}
              </div>
              {(dlWarehouses.warehouses || []).some(w => !w.linked_in_our_db) && (
                <p className="mt-1.5 text-amber-700 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3 shrink-0" />
                  Warehouses marked <b>!</b> exist in Delhivery but are NOT linked to any vendor location. Use <b>Link</b> on the vendor row below to fix AWB failures.
                </p>
              )}
            </div>
          )}

          {/* Per-vendor detail table */}
          {showDetails && stats?.details && (
            <div className="mt-3 border-t pt-3 max-h-[600px] overflow-y-auto">
              {stats.test_data_skipped_count > 0 && (
                <p className="text-[11px] text-gray-500 mb-2">
                  Skipped {stats.test_data_skipped_count} test-data row(s) — clean these from <code>db.vendor_locations</code> when convenient.
                </p>
              )}
              <table className="w-full text-xs">
                <thead className="text-left text-gray-500 sticky top-0 bg-white z-10">
                  <tr>
                    <th className="py-1 font-medium">Vendor</th>
                    <th className="py-1 font-medium">City · Pin</th>
                    <th className="py-1 font-medium">Warehouse in DB</th>
                    <th className="py-1 font-medium">Status</th>
                    <th className="py-1 font-medium">Reason</th>
                    <th className="py-1 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.details.map((d, i) => {
                    const mismatch = isMismatched(d);
                    return (
                      <tr key={d.location_id || i}
                        className={`border-t ${mismatch ? 'bg-amber-50' : 'border-gray-100'}`}
                        data-testid={`backfill-row-${d.location_id || i}`}>
                        <td className="py-1 pr-2 max-w-[200px]">
                          <div className="font-medium text-gray-900 truncate" title={d.vendor_business_name}>
                            {d.vendor_business_name || '—'}
                          </div>
                          {d.vendor_email && <div className="text-[10px] text-blue-600 truncate">{d.vendor_email}</div>}
                          <div className="text-[9px] text-gray-400">{(d.vendor_id || '').slice(0, 8)}…</div>
                        </td>
                        <td className="py-1 pr-2 text-gray-700 whitespace-nowrap">
                          {d.city || '—'}
                          {d.pincode && <span className="text-gray-400"> · {d.pincode}</span>}
                        </td>
                        <td className="py-1 pr-2 max-w-[160px]">
                          <span className={`font-mono text-[11px] truncate block ${mismatch ? 'text-amber-700 font-bold' : ''}`}
                            title={d.warehouse_name}>
                            {d.warehouse_name || '—'}
                          </span>
                          {mismatch && (
                            <span className="text-[10px] text-amber-600 flex items-center gap-0.5 mt-0.5">
                              <AlertTriangle className="h-2.5 w-2.5" /> Not in Delhivery
                            </span>
                          )}
                        </td>
                        <td className="py-1 pr-2 whitespace-nowrap">
                          {mismatch
                            ? <span className="text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> mismatch</span>
                            : d.status === 'ok'
                              ? <span className="text-green-700">✓ ok</span>
                              : <span className="text-amber-700">⚠ failed</span>}
                        </td>
                        <td className="py-1 pr-2 max-w-[200px]">
                          {d.missing_fields?.length > 0
                            ? <div className="flex flex-wrap gap-1">
                                {d.missing_fields.map(f => (
                                  <Badge key={f} variant="outline" className="text-[10px] border-rose-300 bg-rose-50 text-rose-700">missing {f}</Badge>
                                ))}
                              </div>
                            : <span className="text-gray-600 text-[11px] truncate block" title={d.message}>{d.message}</span>}
                        </td>
                        <td className="py-1 space-y-1 min-w-[90px]">
                          {/* Link button — always shown, most critical for mismatch */}
                          {d.location_id && (
                            <Button size="sm" variant={mismatch ? 'default' : 'outline'}
                              className={`h-6 text-[10px] px-2 block w-full ${mismatch ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}`}
                              onClick={() => openLinkModal(d)}
                              data-testid={`link-warehouse-${d.location_id}`}
                              title="Assign an existing active Delhivery warehouse name to this vendor — fixes 'non serviceable' AWB errors">
                              <Link2 className="h-2.5 w-2.5 mr-1" /> Link
                            </Button>
                          )}
                          {d.status === 'failed' && d.location_id && (!d.missing_fields?.length) && (
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 block w-full"
                              disabled={registeringId === d.location_id}
                              onClick={() => registerOne(d)}
                              data-testid={`register-one-${d.location_id}`}>
                              {registeringId === d.location_id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Retry'}
                            </Button>
                          )}
                          {d.location_id && (
                            <Button size="sm" variant="ghost"
                              className="h-6 text-[10px] px-2 text-amber-700 hover:bg-amber-50 block w-full"
                              disabled={registeringId === d.location_id}
                              onClick={() => registerOne(d, true)}
                              data-testid={`force-reregister-${d.location_id}`}
                              title="Force re-register fresh — creates a new warehouse name">
                              {registeringId === d.location_id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Force Re-reg'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Link Warehouse Modal */}
      {linkRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          data-testid="link-warehouse-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-semibold text-base flex items-center gap-2">
                  <Link2 className="h-4 w-4" /> Link Delhivery Warehouse
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  Vendor: <b>{linkRow.vendor_business_name || linkRow.vendor_id}</b>
                </p>
                <p className="text-xs text-gray-500">
                  City: {linkRow.city} · Pin: {linkRow.pincode}
                </p>
              </div>
              <button onClick={() => setLinkRow(null)} className="text-gray-400 hover:text-gray-600 mt-0.5">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Current stored name */}
            <div className="mb-4 rounded-lg border bg-gray-50 p-3 text-sm">
              <p className="text-xs text-gray-500 mb-1">Currently stored in our DB</p>
              <p className={`font-mono ${isMismatched(linkRow) ? 'text-amber-700 font-bold' : 'text-gray-800'}`}>
                {linkRow.warehouse_name || <span className="text-gray-400 italic">none</span>}
              </p>
              {isMismatched(linkRow) && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  This name is NOT in Delhivery's active warehouse list — causing AWB failures.
                </p>
              )}
            </div>

            {/* Delhivery live warehouses picker */}
            {loadingDl ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading active warehouses from Delhivery…
              </div>
            ) : dlWarehouses?.warehouses?.length > 0 ? (
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-600 mb-2">
                  Select from active Delhivery warehouses
                  <span className="text-gray-400 ml-1">(or type manually below)</span>
                </p>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {dlWarehouses.warehouses.map(w => (
                    <button key={w.name} type="button"
                      onClick={() => setLinkName(w.name)}
                      className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition-colors ${
                        linkName === w.name
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : 'border-gray-200 hover:border-gray-400 bg-white'
                      }`}
                      data-testid={`pick-warehouse-${w.name}`}>
                      <div className="font-mono text-xs font-semibold">{w.name}</div>
                      <div className={`text-[11px] mt-0.5 ${linkName === w.name ? 'text-gray-300' : 'text-gray-500'}`}>
                        {w.city} · {w.pin}
                        {w.linked_in_our_db && <span className={linkName === w.name ? ' text-green-300' : ' text-green-600'}> · already linked elsewhere</span>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-amber-600 mb-3 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Could not load live list — type the warehouse name manually.
              </p>
            )}

            {/* Manual input */}
            <div className="mb-5">
              <label className="text-xs font-medium text-gray-600 block mb-1">
                Delhivery warehouse name to link
              </label>
              <input
                type="text"
                value={linkName}
                onChange={e => setLinkName(e.target.value)}
                placeholder="e.g. aniyor-VEN-0090-66c5"
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-900"
                data-testid="link-warehouse-name-input"
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setLinkRow(null)}>Cancel</Button>
              <Button className="flex-1" disabled={!linkName.trim() || linking} onClick={confirmLink}
                data-testid="confirm-link-warehouse-btn">
                {linking ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Linking…</> : 'Confirm Link'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
